import CTNButton from '@ctn/ui/components/ctn-button';
import CTNDropdownButton from '@ctn/ui/components/ctn-dropdown-button';
import CTNEllipsis from '@ctn/ui/components/ctn-ellipsis';
import { CTNIcon, CTNIconTooltip } from '@ctn/ui/components/ctn-icon';
import CTNInput from '@ctn/ui/components/ctn-input';
import CTNPagination from '@ctn/ui/components/ctn-pagination';
import CTNTag from '@ctn/ui/components/ctn-tag';
import { type CTNTooltipProps } from '@ctn/ui/types/ctn-tooltip';
import parseHTML from 'html-react-parser';
import { type ReactElement, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import PageHeader from '@/components/PageHeader';
import { usePageContext } from '@/context/page.context';
import { getListadoMensajes, putMessageArchivado, putMessageEstado } from '@/services/mensajes.service';

import { dropdownPriorityOptions } from '@/constants/empresa.constants';
import { ROUTES_LABEL } from '@/constants/routes.constants';
import { getUrlTramite } from '@/helpers/navigation';
import type { CTNPaginationRef } from '@ctn/ui/types/ctn-pagination';

import { useGlobalContext } from '../../context/global.context';

const COLOR_PRIORIDAD: Record<number, string> = { 1: 'red-50', 2: 'orange-50', 3: 'blue-50' };
const PAGE_SIZE = { MENSAJES: 10 };
const DEFAULT_VALUE = { listado: [], totalResults: 0 };

type TipoDescripcion = { id: number; descripcion: string };
type Notificacion = {
	id: number;
	asunto: string;
	cuerpo: string;
	idTramiteExterno: number;
	idElemento: number;
	fechaCreacion: string;
	fechaNotificacion?: string;
	tipoNotificacion: TipoDescripcion;
	tipoTramite: TipoDescripcion;
	prioridad: TipoDescripcion;
	origen: TipoDescripcion;
};
type Notificaciones = { archivada?: boolean; leida: boolean; notificacion: Notificacion };
type ListadoNotificaciones = { listado: Notificaciones[]; totalResults: number };

const Mensajes = (): ReactElement => {
	const { user } = useGlobalContext();
	const { updateMessages, setBreadcrumb } = usePageContext();
	const navigate = useNavigate();

	const [mensajes, setMensajes] = useState<ListadoNotificaciones>(DEFAULT_VALUE);
	const [mensajeActivo, setMensajeActivo] = useState<Notificaciones | undefined>(undefined);
	const [listaMensajesLeidos, setListaMensajesLeidos] = useState<number[]>([]);
	const [isFilter, setIsFilter] = useState<boolean>(false);
	const [searchTerm, setSearchTerm] = useState<string>('');
	const [checkedItems, setCheckedItems] = useState<any[]>([]);
	const [filters, setFilters] = useState({ leidos: false, noLeidos: false, archivados: false });
	const [page, setPage] = useState(0);
	const [canNextPage, setCanNextPage] = useState(false);
	const [canPreviousPage, setCanPreviousPage] = useState(false);
	const [disabled, setDisabled] = useState(false);
	const [refreshTick, setRefreshTick] = useState(0);
	const showIcon = false;

	const refPagination = useRef<CTNPaginationRef>(null);
	const { initBreadcrumb } = usePageContext();

	const {
		control,
		formState: { errors },
	} = useForm<{ buscador: string }>({
		mode: 'onSubmit',
		defaultValues: { buscador: '' },
	});

	const normalizeText = (text: string): string =>
		text
			?.normalize('NFD')
			.replace(/[\u0300-\u036f]/g, '')
			.toLowerCase() || '';

	const transformData = (data: any[]) =>
		(data ?? [])
			.filter((item: any) => item.contratado === true)
			.map((item: any) => ({
				...item,
				tipoNotificaciones: [],
			}));

	const filteredData = transformData(user?.listaTramitesEstadoContratacion ?? []).filter((item: any) => {
		const searchTermNormalized = normalizeText(searchTerm);
		const matchesTramite = normalizeText(item.tipoTramite.descripcion).includes(searchTermNormalized);
		const matchesDescripcion = normalizeText(item.descripcionAmpliada).includes(searchTermNormalized);
		const matchesTag = normalizeText(item.tag || '').includes(searchTermNormalized);

		return matchesTramite || matchesDescripcion || matchesTag;
	});

	const getMessagesData = async (payload: any) => {
		setDisabled(true);
		setMensajeActivo(undefined);
		const { data, code }: { data: any; code: number } = await getListadoMensajes(payload);
		if (code === 200) {
			setMensajes(data);
			const resultado = data?.listado?.filter((opt: any) => opt?.leida).map((opt: any) => opt?.notificacion?.id);
			setListaMensajesLeidos(resultado || []);
		}
		setDisabled(false);
	};

	const handleCheckboxChange = (tramiteId: number) => {
		const isChecked = checkedItems.some((item: any) => item.tramiteId === tramiteId);
		const updatedCheckedItems = isChecked
			? checkedItems.filter((item: any) => item.tramiteId !== tramiteId)
			: [...checkedItems, { tramiteId }];

		setCheckedItems(updatedCheckedItems);
		refreshData();
	};

	const handleFilterChange = (filterName: string) => {
		setFilters((prev: any) => ({ ...prev, [filterName]: !prev[filterName] }));
		refreshData();
	};

	const refreshData = () => {
		setPage(0);
		setRefreshTick((prev) => prev + 1);
		refPagination.current?.goToPage(1);
	};

	const estadoMensaje = async (estado: boolean, id: number) => {
		const dataParam = {
			estado,
			listaIdsNotificaciones: [id],
		};
		const { code }: any = await putMessageEstado(dataParam);
		if (code === 200) {
			if (estado) {
				setListaMensajesLeidos((prev) => [...prev, id]);
			} else {
				setListaMensajesLeidos((prev) => prev.filter((num) => num !== id));
				setMensajeActivo(undefined);
				refreshData();
			}
			updateMessages();
		}
	};

	const archivar = async (estado: boolean, id: number) => {
		const dataParam = {
			estado,
			listaIdsNotificaciones: [id],
		};
		const { code }: { code: number } = await putMessageArchivado(dataParam);
		if (code === 200) {
			refreshData();
			updateMessages();
		}
	};

	const clickMensaje = (mensaje: Notificaciones) => {
		if (!listaMensajesLeidos.includes(mensaje?.notificacion?.id)) {
			estadoMensaje(true, mensaje?.notificacion?.id);
		}
		setMensajeActivo(mensaje);
		if (!listaMensajesLeidos.includes(mensaje?.notificacion?.id)) {
			setListaMensajesLeidos([...listaMensajesLeidos, mensaje?.notificacion?.id]);
		}
		setIsFilter(false);
	};

	const verDetalle = () => {
		const data = user?.listaTramitesEstadoContratacion.find(
			(el) => el.tipoTramite.id === mensajeActivo?.notificacion?.tipoTramite?.id,
		);
		const url = `${getUrlTramite(data)}/solicitudes/${mensajeActivo?.notificacion?.idTramiteExterno}`;
		setBreadcrumb(undefined);
		navigate(url);
	};

	const pageChange = (e: number) => {
		setPage(e - 1);
	};

	useEffect(() => {
		const { archivados, leidos, noLeidos } = filters;
		const leidosParte2 = !leidos && noLeidos ? false : null;
		const currentPayload = {
			fechaDesde: null,
			fechaHasta: null,
			listaIdsTipoTramite: checkedItems.map((item: any) => item.tramiteId),
			listaIdsTipoNotificacion: [],
			listaIdsPrioridad: [1, 2, 3],
			archivada: archivados,
			leida: leidos && !noLeidos ? true : leidosParte2,
			start: page === 0 ? 0 : PAGE_SIZE.MENSAJES * page,
			limit: 10,
		};

		getMessagesData(currentPayload);
	}, [checkedItems, filters, page, refreshTick]);

	useEffect(() => {
		const totalPages = Math.ceil((mensajes?.totalResults || 0) / PAGE_SIZE.MENSAJES) - 1;
		setCanNextPage(page < totalPages);
		setCanPreviousPage(page < totalPages + 1 && page !== 0);
	}, [page, mensajes]);

	const getTag = (id: number) => {
		const tag = user?.listaTramitesEstadoContratacion?.find((el) => el.tipoTramite.id === id)?.tag ?? '';
		return tag;
	};

	const tooltip: CTNTooltipProps = {
		maxWidth: 200,
		place: 'top',
	};

	useEffect(() => {
		initBreadcrumb(ROUTES_LABEL.MENSAJES);
	}, []);

	return (
		<section className="c-pnp-operations">
			<header className="c-pnp-creg__header">
				<div className="c-pnp-creg__title-container">
					<PageHeader title="Mis mensajes" />
				</div>
			</header>

			<div className="c-pnp-inbox u-mt-24">
				<div className="c-pnp-inbox__sidebar">
					{isFilter && (
						<div className="c-pnp-filter-inbox c-pnp-filter-inbox__content">
							<div className="c-pnp-filter-inbox__header c-pnp-filter-inbox__header--open">
								<div className="c-pnp-filter-inbox__title">
									<CTNIcon size="md" icon="chat" color="gray-60" />
									<p className="c-pnp-inbox__text">Todos los mensajes</p>
								</div>
								<CTNIcon
									size="md"
									icon="arrowUp"
									color="gray-80"
									className="c-pnp-icon__action"
									onClick={() => setIsFilter(!isFilter)}
								/>
							</div>
							<div className="c-pnp-filter-inbox__input">
								{/* @ts-expect-error pass */}
								<CTNInput
									condensed
									name="buscador"
									placeholder="Buscar"
									iconRight={{ icon: 'search' }}
									control={control}
									error={errors.buscador?.message}
									onChange={(e: any) => {
										setSearchTerm(e.target.value);
										refreshData();
									}}
								/>
								<div className="c-pnp-filter-inbox__search">
									<div className="c-pnp-filter-inbox__item-row">
										{[
											{ label: 'Leídos', key: 'leidos' },
											{ label: 'No leídos', key: 'noLeidos' },
											{ label: 'Archivados', key: 'archivados' },
										].map(({ label, key }) => (
											<div key={key} className="c-pnp-filter-inbox__checkgroup">
												<input
													className='c-pnp-filter-inbox__checkbox'
													type="checkbox"
													checked={(filters as any)[key]}
													onChange={() => handleFilterChange(key)}
												/>
												<label>{label}</label>
											</div>
										))}
									</div>
									{filteredData.map((tramite: any) => (
										<div
											key={`tramite_${tramite?.tipoTramite?.id}`}
											className="c-pnp-filter-inbox__item-row u-mt-16"
										>
											<div className="c-pnp-filter-inbox__checkgroup">
												<input
													className="c-pnp-filter-inbox__checkbox"
													type="checkbox"
													checked={checkedItems.some(
														(item: any) => item.tramiteId === tramite.tipoTramite.id,
													)}
													onChange={() => handleCheckboxChange(tramite.tipoTramite.id)}
												/>
												<label className='c-pnp-filter-inbox__label'>
													<CTNTag color="grey" size="size-16">
														{getTag(tramite.tipoTramite.id)}
													</CTNTag>
													{tramite.tipoTramite.descripcion}
													&nbsp;
													
												</label>
											</div>
										</div>
									))}
								</div>
							</div>
						</div>
					)}
					<div className="c-pnp-inbox__wrapper">
						<div className="c-pnp-filter-inbox__header">
							<div className="c-pnp-filter-inbox__title">
								<CTNIcon size="md" icon="chat" color="gray-60" />
								<p className='c-pnp-inbox__text'>Todos los mensajes</p>
							</div>
							<CTNIcon
								size="md"
								icon="arrowDown"
								color="gray-80"
								className="c-pnp-icon__action"
								onClick={() => setIsFilter(!isFilter)}
							/>
						</div>
						{mensajes?.listado?.map((mensaje: Notificaciones) => (
							<button
								key={mensaje?.notificacion?.id}
								type="button"
								className={`c-pnp-message-list${mensajeActivo?.notificacion?.id === mensaje?.notificacion?.id ? '--active' : ''} ${listaMensajesLeidos.includes(mensaje?.notificacion?.id) ? 'c-pnp-message--seen' : ''}`}
								tabIndex={0}
								onClick={() => clickMensaje(mensaje)}
								onKeyDown={(e) => {
									if (e.key === 'Enter' || e.key === ' ') {
										e.preventDefault();
										clickMensaje(mensaje);
									}
								}}
							>
								<div className="c-pnp-message-list__indicator">
									{!listaMensajesLeidos.includes(mensaje?.notificacion?.id) && (
										<CTNIcon size="md" icon="status" color="blue-50" />
									)}
								</div>
								<div className='c-pnp-message-list__group'>
									<div className="c-pnp-message-list__text">
										
										<CTNTag color="grey" size="size-16">
											{getTag(mensaje?.notificacion?.tipoTramite?.id)}
										</CTNTag>
										<strong><CTNEllipsis content={mensaje?.notificacion?.asunto} /></strong>
										{showIcon && (
											<CTNIcon
												size="md"
												icon="status"
												color={COLOR_PRIORIDAD[mensaje?.notificacion?.prioridad?.id]}
											/>
										)}
									</div>
									<div className="c-pnp-message-list__desc u-mt-8">
										<CTNEllipsis content={mensaje?.notificacion?.tipoNotificacion?.descripcion} maxWidth={100}/>
										<div>
											{(mensaje?.notificacion?.fechaNotificacion ?? mensaje?.notificacion?.fechaCreacion)?.split(' ')[0] || ''}
										</div>
									</div>
								</div>
								<div className='c-pnp-message-list__priority'>
									<CTNIconTooltip
										size="md" icon="status" color={dropdownPriorityOptions.find((it) => it.value === mensaje.notificacion.prioridad.id)?.iconColor}
										tooltip={{...tooltip, content: `Prioridad ${dropdownPriorityOptions.find((it) => it.value === mensaje.notificacion.prioridad.id)?.label.toLowerCase()}` }}
									/>
								</div>
							</button>
						))}
					</div>

					<div className="c-pnp-inbox__pagination">
						<CTNPagination
							ref={refPagination}
							pageSize={PAGE_SIZE.MENSAJES}
							totalData={mensajes?.totalResults}
							tablePagination={{
								pageIndex: page,
								getCanNextPage: () => !disabled && canNextPage,
								getCanPreviousPage: () => !disabled && canPreviousPage,
								setPageIndex: (e: number) => setPage(e),
							}}
							onPageChange={(e: number) => pageChange(e)}
						/>
					</div>
				</div>

				{mensajeActivo ? (
					<div className="c-pnp-message">
						<div className="c-pnp-message__header">
							<div className='c-pnp-message__subject'>
								<div className='c-pnp-message__content-subj'>
									<CTNTag color="grey" size="size-16">
										{getTag(mensajeActivo?.notificacion?.tipoTramite?.id)}
									</CTNTag>
									<h4><strong>{mensajeActivo?.notificacion?.asunto}</strong></h4>
									<CTNIconTooltip
										size="md"
										icon="status"
										color={COLOR_PRIORIDAD[mensajeActivo?.notificacion?.prioridad?.id]}
										tooltip={{...tooltip, content: `Prioridad ${dropdownPriorityOptions.find((it) => it.value === mensajeActivo.notificacion.prioridad.id)?.label.toLowerCase()}` }}
									/>
								</div>
								<CTNDropdownButton
									variant="ghost"
									mode="secondary"
									size="size-32"
									icon={[
										{
											icon: 'moreHorizontal',
											color: 'blue',
											position: 'left',
										},
									]}
									items={[
										{
											label: 'Marcar como no leida',
											onClick() {
												void estadoMensaje(
													false,
													mensajeActivo?.notificacion?.id,
												);
											},
										},
										{
											label: mensajeActivo?.archivada
												? 'Desarchivar'
												: 'Archivar',
											onClick() {
												void archivar(
													!mensajeActivo?.archivada,
													mensajeActivo?.notificacion?.id,
												);
											},
										},
									]}
								/>
							</div>

							<div className="c-pnp-message__date">
								{mensajeActivo?.notificacion?.fechaNotificacion ?? mensajeActivo?.notificacion?.fechaCreacion}
							</div>
						</div>
						<div className="c-pnp-message__body u-mt-32"> 
							<div className="c-pnp-message__img"> 
								<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none" > <rect width="48" height="48" fill="white" /> <path d="M11.8125 41.7812V9.28125H22.8921V32.4874C22.8921 32.4874 13.3104 32.7205 11.8125 41.7812Z" fill="#1975D2" /> <path d="M25.108 41.7812V9.28125H36.1875V32.4874C36.1875 32.4874 26.6059 32.7205 25.108 41.7812Z" fill="#1975D2" /> </svg> 
								<div className="c-pnp-message__logo"> 
									<p className="c-pnp-message__title">Sede electrónica notarial</p> 
									<p className="c-pnp-message__subtitle">portal empresas y profesionales</p> 
								</div> 
							</div>
						</div>
						<div className="c-pnp-message__body u-mt-24">
							<div className="c-pnp-message__txt">
								{parseHTML(
									mensajeActivo?.notificacion?.cuerpo.replace(
										/Arial/g,
										'Open Sans',
									),
								)}
							</div>
							<div className='c-pnp-message__footer'>
								<CTNButton
									className="u-mt-16"
									minWidth="100px"
									mode="secondary"
									variant="outline"
									label="Ver detalle"
									size="md"
									onClick={verDetalle}
								/>
							</div>
				
						</div>
					</div>
				) : (
					<div className="c-pnp-inbox__content-empty">
						<p className='c-pnp-inbox__text'>
							{mensajes?.listado?.length === 0
								? 'No se han encontrado mensajes'
								: 'Selecciona un mensaje para ver el detalle'}
						</p>
					</div>
				)}
			</div>
		</section>
	);
};

export default Mensajes;
